'use client';

import { useEffect, useContext, FC, memo, PropsWithChildren } from 'react';
import StepWithMaybeResponse from '../StepWithMaybeResponse';
import { ResetGlobalStyles } from '../TempComponents';
import { LinterPanel } from '../linter/LinterPanel';
import { LinterHelper } from '../linter/LinterHelper';
import { LinterBackdrop } from '../linter/LinterBackdrop';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { SideDrawer } from '../SideDrawer';
import { Icon, Typography, Markdown, SegmentedButtons } from '@scale/scaleui';
import { ToolboxSection, useChatLayoutStore, ChatLayout } from '../hooks/useChatLayoutStore';
import { useWindowSize } from 'react-use-v13';
import { usePinStore } from '../hooks/usePinStore';
import { twMerge } from 'tailwind-merge';
import {
  useAfterSteps,
  useBeforeSteps,
  useChatStoreExtract,
  useTurnIndexes,
} from '../../Chat/hooks/ChatStore.hooks';
import { GlobalStepWidth } from '../../Chat/hooks/types';
import { useFieldRubricStore } from '../hooks/useFieldRubricStore';
import { ChatRendererProps } from './types';
import { TurnRenderer } from './TurnRenderer';
import { PinnedItemsPanel } from '../PinnedItemsPanel';
import { PinnedItemsPanel as MultiChatPinnedItemsPanel } from 'react-scale/components/scalers/tasks/MultiChat/components/pinnedItems/PinnedItemsPanel';
import { ChatTaskParamsStages } from '@scale/shared/chat/steps';
import cn from 'classnames';

type ChatInlineRendererProps = ChatRendererProps;

const SideToolboxHandler = () => {
  const { taskId } = useContext(ChatTaskContext);
  const setStepWidth = useChatStoreExtract(state => state.setStepWidth);

  const resetHasDrawerBeenOpened = useChatLayoutStore(state => state.resetHasDrawerBeenOpened);
  const setLayout = useChatLayoutStore(state => state.setLayout);
  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);
  const setSelectedToolboxTab = useChatLayoutStore(state => state.setSelectedToolboxTab);

  const rubric = useFieldRubricStore(state => state.rubric);
  const setRubric = useFieldRubricStore(state => state.setRubric);

  const resetPinStore = usePinStore(state => state.reset);

  // Reset all pinned items when the task changes
  useEffect(() => {
    resetPinStore();
    resetHasDrawerBeenOpened();
  }, [taskId, resetPinStore, resetHasDrawerBeenOpened]);

  useEffect(() => {
    if (rubric) {
      setSelectedToolboxTab(ToolboxSection.Rubric);
    } else {
      setSelectedToolboxTab(ToolboxSection.Pinned);
    }
  }, [rubric, setSelectedToolboxTab]);

  // Set the correct inline layout conditions since the interaction component can change
  useEffect(() => {
    setIsDrawerOpen(false);
    setRubric(null);
    setLayout(ChatLayout.Inline);
    setStepWidth(GlobalStepWidth.Dynamic);
  }, [setIsDrawerOpen, setRubric, setLayout, setStepWidth]);

  return null;
};

const ChatInlineSidebar = ({ readOnly, demoMode }: ChatRendererProps) => {
  // MultiChat -> this is undefined if not a multi-chat task.
  const multiChatIndex = useChatStoreExtract(state => state.currentMultiChatIndex);

  // Update response using this extract
  const setRubric = useFieldRubricStore(state => state.setRubric);
  const rubric = useFieldRubricStore(state => state.rubric);

  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);
  const selectedToolboxTab = useChatLayoutStore(state => state.selectedToolboxTab);
  const setSelectedToolboxTab = useChatLayoutStore(state => state.setSelectedToolboxTab);

  const drawerWidth = useChatLayoutStore(state => state.drawerWidth);
  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);

  return (
    <SideDrawer
      width={drawerWidth}
      open={isDrawerOpen}
      resizable={true}
      className={cn({ 'top-[7rem] h-[calc(100%-7rem)]': demoMode })}
      header={
        <div className="p-4 border-b border-neutral-200 flex justify-between items-center">
          <Typography.Subtitle2>Toolbox</Typography.Subtitle2>
          <div>
            <Icon
              variant="white"
              icon="times"
              hasBackground
              onClick={() => {
                setIsDrawerOpen(false);
                setRubric(null);
              }}
            />
          </div>
        </div>
      }
    >
      <div className="pb-4">
        <SegmentedButtons
          buttons={[
            {
              id: ToolboxSection.Pinned,
              label: 'Pinned Items',
            },
            ...(rubric ? [{ id: ToolboxSection.Rubric, label: 'Rubric' }] : []),
          ]}
          onSelect={r => setSelectedToolboxTab(r as ToolboxSection)}
          selected={selectedToolboxTab as string}
        />
      </div>
      {selectedToolboxTab === ToolboxSection.Rubric ? (
        <div>
          <Markdown markdown={rubric || ''} enableHtml={true} />
        </div>
      ) : multiChatIndex === undefined ? (
        // Single Chat
        <PinnedItemsPanel readOnly={readOnly} />
      ) : (
        // MultiChat
        <MultiChatPinnedItemsPanel />
      )}
    </SideDrawer>
  );
};

const ChatInlineRendererContainer = memo(function ChatInlineRendererContainer({
  children,
}: PropsWithChildren<{}>) {
  const { width } = useWindowSize();
  const { enableUnifiedFeedback } = useContext(ChatTaskContext);
  const getCurrentDrawerWidth = useChatLayoutStore(state => state.getCurrentDrawerWidth);
  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);

  const drawerWidth = useChatLayoutStore(state => state.drawerWidth);

  return (
    <div
      className={twMerge(
        'flex flex-col py-4 gap-4 w-full',
        enableUnifiedFeedback ? 'items-left' : 'items-center',
      )}
      style={{
        marginRight: getCurrentDrawerWidth(),
        transition: 'margin 200ms ease-in-out',
        width:
          !enableUnifiedFeedback && isDrawerOpen
            ? Math.min(1250, width - drawerWidth - 108)
            : undefined,
      }}
    >
      {children}
    </div>
  );
});

export const ChatInlineRenderer: FC<ChatInlineRendererProps> = ({
  readOnly,
  preventScrollToBottom,
  displayStage,
  demoMode,
}) => {
  const beforeSteps = useBeforeSteps();
  const turnIndexes = useTurnIndexes();
  const afterSteps = useAfterSteps();

  return (
    <ResetGlobalStyles>
      <LinterPanel />
      <LinterBackdrop />
      <LinterHelper />
      <SideToolboxHandler />
      <ChatInlineRendererContainer>
        {(displayStage === undefined || displayStage?.stage === ChatTaskParamsStages.Before) &&
          beforeSteps.map(step => (
            <StepWithMaybeResponse
              key={`before-step-${step.responseIndex}`}
              responseIndex={step.responseIndex}
              readOnly={readOnly}
              preventScrollToBottom={preventScrollToBottom}
            />
          ))}

        {turnIndexes.map(turnIndex => {
          if (displayStage !== undefined && turnIndex !== displayStage.turnIndex) {
            return null;
          }

          return (
            <TurnRenderer
              key={`turn-${turnIndex}`}
              turnIndex={turnIndex}
              readOnly={readOnly}
              preventScrollToBottom={preventScrollToBottom}
            />
          );
        })}

        {(displayStage === undefined || displayStage?.stage === ChatTaskParamsStages.After) &&
          afterSteps.map(step => (
            <StepWithMaybeResponse
              key={`after-step-${step.responseIndex}`}
              responseIndex={step.responseIndex}
              readOnly={readOnly}
              preventScrollToBottom={preventScrollToBottom}
            />
          ))}
      </ChatInlineRendererContainer>

      <ChatInlineSidebar readOnly={readOnly} demoMode={demoMode} />
    </ResetGlobalStyles>
  );
};
